import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Link from 'redux-first-router-link'
import Menu from '@groovehq/internal-design-system/lib/components/Dropdown/DropdownMenu'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import NewLabel from '@groovehq/internal-design-system/lib/components/NewLabel/NewLabel'
import { divider } from '@groovehq/internal-design-system/lib/styles/elements'
import { selectAccountPreferenceDebugFrontend } from 'selectors/app/selectAccountPreferences'
import {
  selectCurrentUser,
  selectCurrentUserCanAccessAdmin,
  selectCurrentUserIsAdminOrOwner,
} from 'ducks/currentUser/selectors'
import { selectSettingsLandingPageAction } from 'selectors/menus'
import { selectShouldHideNewSettings } from 'ducks/flags'
import DebugButton from 'components/DebugButton'
import debug from 'util/debug'
import LegacyLink from 'components/LegacyLink'
import Avatar from 'components/Avatar'
import { LOGOUT_PAGE } from 'constants/pages'
import {
  SETTINGS_PROFILE_PAGE,
  SETTINGS_BILLING_PAGE,
} from 'subapps/settings/types'
import {
  profile as ProfileIcon,
  billing as BillingIcon,
  inviteTeammate as InviteTeammateIcon,
  logout as LogoutIcon,
  settings as SettingsIcon,
} from 'assets/icons/groove/v2'
import { useDrawer } from 'ducks/drawers/hooks'
import { DRAWER_TYPE_AGENT_ADD } from 'ducks/drawers/types'
import ItemWithIcon from './ItemWithIcon'
import { styles } from './styles'

const TO_SETTINGS_PROFILE_PAGE = {
  type: SETTINGS_PROFILE_PAGE,
}

const TO_SETTINGS_BILLING_PAGE = {
  type: SETTINGS_BILLING_PAGE,
}

const FullMenu = React.memo(() => {
  const dispatch = useDispatch()
  const canDebugFrontend = useSelector(selectAccountPreferenceDebugFrontend)
  const isDebug = canDebugFrontend || debug.enabled
  const isSuperAdmin = useSelector(selectCurrentUserCanAccessAdmin)
  const isManager = useSelector(selectCurrentUserIsAdminOrOwner)
  const settingsLandingPageAction = useSelector(selectSettingsLandingPageAction)
  const shouldHideNewSettings = useSelector(selectShouldHideNewSettings)
  const hasBottomItems = isSuperAdmin || isDebug

  const { openDrawer: openAddDrawer } = useDrawer({
    type: DRAWER_TYPE_AGENT_ADD,
    lazyLoad: true,
  })

  const handleAddTeammates = useCallback(
    () => {
      openAddDrawer('new', {
        additionalProps: { title: `Add ${app.t('Agent')}` },
      })
    },
    [openAddDrawer]
  )

  const handleLogout = useCallback(
    () => {
      dispatch({ type: LOGOUT_PAGE })
    },
    [dispatch]
  )

  return (
    <Menu as="div" css={styles.menuStyle}>
      <Link className="grui" to={TO_SETTINGS_PROFILE_PAGE}>
        <Menu.Item itemKey="my-profile" className="grui">
          <ItemWithIcon icon={<ProfileIcon />}>View profile</ItemWithIcon>
        </Menu.Item>
      </Link>
      <Link className="grui" to={settingsLandingPageAction} shouldDispatch>
        <Menu.Item itemKey="settings" className="grui">
          <ItemWithIcon icon={<SettingsIcon />}>Settings</ItemWithIcon>
          {!shouldHideNewSettings && (
            <NewLabel
              className="grui ml-5 mt-1 align-middle"
              isInfiniteAnimation
              size="small"
            />
          )}
        </Menu.Item>
      </Link>
      {isManager && (
        <Link className="grui" to={TO_SETTINGS_BILLING_PAGE}>
          <Menu.Item itemKey="billing" className="grui">
            <ItemWithIcon icon={<BillingIcon />}>Billing</ItemWithIcon>
          </Menu.Item>
        </Link>
      )}
      {isManager && (
        <Menu.Item
          itemKey="invite-teammates"
          className="grui"
          onClick={handleAddTeammates}
          css={styles.highlightMenuItem}
        >
          <ItemWithIcon icon={<InviteTeammateIcon className="sm-icon" />}>
            {`Invite ${app.t('team')} members`}
          </ItemWithIcon>
        </Menu.Item>
      )}
      <div css={divider.styles.primary} role="separator" />
      <Menu.Item
        itemKey="logout"
        as="button"
        type="button"
        onClick={handleLogout}
      >
        <ItemWithIcon icon={<LogoutIcon className="sm-icon" />}>
          Log out
        </ItemWithIcon>
      </Menu.Item>
      {hasBottomItems && <div css={divider.styles.primary} role="separator" />}
      {isSuperAdmin && (
        <>
          <LegacyLink
            path="/admin/accounts"
            className="grui"
            async={false}
            skipModal
          >
            <Menu.Item itemKey="go-to-admin" className="grui">
              Go to admin
            </Menu.Item>
          </LegacyLink>
          <LegacyLink path="/sidekiq" className="grui" async={false} skipModal>
            <Menu.Item itemKey="go-to-sidekiq" className="grui">
              Sidekiq Dashboard
            </Menu.Item>
          </LegacyLink>
        </>
      )}
      {isDebug && <DebugButton as={Menu.Item} />}
    </Menu>
  )
})

const SettingsMenu = React.memo(() => {
  const currentUser = useSelector(selectCurrentUser)

  return (
    <Dropdown
      overlay={<FullMenu />}
      position="bottom-end"
      strategy="fixed"
      offset={8}
      autoHeight
      menuItemIconHidden
    >
      <div css={styles.avatarContainer} className="grui ml-7 flex items-center">
        <Avatar size={32} actor={currentUser} />
      </div>
    </Dropdown>
  )
})

export default SettingsMenu
